import React, { useContext, useEffect } from "react";
import { Select, Stack, Grid, Text } from "@mantine/core";
import FloatingLabelInput from "./floating-label-input";
import { DataContext } from "../context/DataContext";

const AddressInput = ({
  onAddressChange,
  value,
  showCityState = true,
  // When true, City / State / Zip stack vertically instead of sharing one
  // cramped row. Now defaults to true since the single-page data-collection
  // forms give each input its own line — call sites can still pass
  // `stacked={false}` to opt into the legacy inline row layout.
  stacked = true,
  // Hint for the on-screen keyboard's action key (e.g. "next"). Optional so
  // inline usages are unaffected.
  enterKeyHint,
  streetLabel = "Street Address",
  cityLabel = "City",
  stateLabel = "State",
  zipLabel = "Zip",
  zipCodeLabel = "Zip Code",
  errorMessage = "Address is required",
  disabled = false,
  ...props
}) => {
  const { data } = useContext(DataContext);

  // Controlled by the parent's form values (passed via `value`), falling back
  // to any saved data. Keeping the parent as the single source of truth means
  // the inputs survive unmount/remount when navigating between steps, instead
  // of being reset from (empty) context.
  const safeValue = value && typeof value === "object" ? value : {};
  const address = {
    addr: safeValue.addr ?? data?.addr ?? "",
    city: safeValue.city ?? data?.city ?? "",
    state: safeValue.state ?? data?.state ?? "",
    zip: safeValue.zip ?? data?.zip ?? "",
  };

  const handleInputChange = (field, fieldValue) => {
    onAddressChange({ ...address, [field]: fieldValue });
  };

  // Seed the parent's form values once on mount from the resolved address
  // (value prop, falling back to saved data). On remount this pushes back the
  // already-preserved values, so navigating away and back never clears them.
  useEffect(() => {
    onAddressChange(address);
  }, []);

  // value = USPS code (stored/submitted), label = full name (shown + searched).
  const states = [
    { value: "AL", label: "Alabama" },
    { value: "AK", label: "Alaska" },
    { value: "AZ", label: "Arizona" },
    { value: "AR", label: "Arkansas" },
    { value: "CA", label: "California" },
    { value: "CO", label: "Colorado" },
    { value: "CT", label: "Connecticut" },
    { value: "DC", label: "District of Columbia" },
    { value: "DE", label: "Delaware" },
    { value: "FL", label: "Florida" },
    { value: "GA", label: "Georgia" },
    { value: "HI", label: "Hawaii" },
    { value: "ID", label: "Idaho" },
    { value: "IL", label: "Illinois" },
    { value: "IN", label: "Indiana" },
    { value: "IA", label: "Iowa" },
    { value: "KS", label: "Kansas" },
    { value: "KY", label: "Kentucky" },
    { value: "LA", label: "Louisiana" },
    { value: "ME", label: "Maine" },
    { value: "MD", label: "Maryland" },
    { value: "MA", label: "Massachusetts" },
    { value: "MI", label: "Michigan" },
    { value: "MN", label: "Minnesota" },
    { value: "MS", label: "Mississippi" },
    { value: "MO", label: "Missouri" },
    { value: "MT", label: "Montana" },
    { value: "NE", label: "Nebraska" },
    { value: "NV", label: "Nevada" },
    { value: "NH", label: "New Hampshire" },
    { value: "NJ", label: "New Jersey" },
    { value: "NM", label: "New Mexico" },
    { value: "NY", label: "New York" },
    { value: "NC", label: "North Carolina" },
    { value: "ND", label: "North Dakota" },
    { value: "OH", label: "Ohio" },
    { value: "OK", label: "Oklahoma" },
    { value: "OR", label: "Oregon" },
    { value: "PA", label: "Pennsylvania" },
    { value: "RI", label: "Rhode Island" },
    { value: "SC", label: "South Carolina" },
    { value: "SD", label: "South Dakota" },
    { value: "TN", label: "Tennessee" },
    { value: "TX", label: "Texas" },
    { value: "UT", label: "Utah" },
    { value: "VT", label: "Vermont" },
    { value: "VA", label: "Virginia" },
    { value: "WA", label: "Washington" },
    { value: "WV", label: "West Virginia" },
    { value: "WI", label: "Wisconsin" },
    { value: "WY", label: "Wyoming" },
  ];

  const selectStyles = {
    input: {
      fontSize: "20px",
      paddingTop: "18px",
    },
    label: {
      position: "absolute",
      pointerEvents: "none",
      fontSize: "11px",
      paddingLeft: "10px",
      paddingTop: "5px",
      zIndex: "1",
      color: "#868e96",
    },
    root: {
      position: "relative",
    },
  };

  return (
    <>
      <Stack spacing="xs">
        {props.error && (
          <Text fz="xs" c="red">
            {errorMessage}
          </Text>
        )}
        <FloatingLabelInput
          placeholder=""
          label={streetLabel}
          value={address.addr}
          onChange={(e) => handleInputChange("addr", e.currentTarget.value)}
          autoComplete="address-line1"
          enterKeyHint={enterKeyHint}
          disabled={disabled}
        />

        {(() => {
          const cityInput = (
            <FloatingLabelInput
              placeholder=""
              label={cityLabel}
              value={address.city}
              onChange={(e) => handleInputChange("city", e.currentTarget.value)}
              autoComplete="address-level2"
              enterKeyHint={enterKeyHint}
              disabled={disabled}
            />
          );

          const stateInput = (
            // Stop Enter from bubbling to the form-level keydown handler so the
            // searchable Select can use Enter to pick an option.
            <div
              onKeyDown={(e) => {
                if (e.key === "Enter") e.stopPropagation();
              }}
              style={{ width: "100%" }}
            >
              <Select
                size="xl"
                searchable
                data={states}
                label={stateLabel}
                placeholder=""
                nothingFound="No matches"
                maxDropdownHeight={280}
                value={address.state || null}
                onChange={(val) => handleInputChange("state", val || "")}
                autoComplete="address-level1"
                disabled={disabled}
                filter={(query, item) => {
                  const q = query.toLowerCase().trim();
                  return (
                    item.label.toLowerCase().includes(q) ||
                    item.value.toLowerCase().includes(q)
                  );
                }}
                styles={selectStyles}
              />
            </div>
          );

          const zipInput = (
            <FloatingLabelInput
              placeholder=""
              label={showCityState ? zipLabel : zipCodeLabel}
              value={address.zip}
              // Keep as text (not number) so leading zeros survive (e.g. 08201)
              // and browser postal-code autofill works.
              onChange={(e) =>
                handleInputChange(
                  "zip",
                  e.currentTarget.value.replace(/\D/g, "").slice(0, 5),
                )
              }
              maxLength={5}
              minLength={5}
              type="text"
              inputMode="numeric"
              pattern="\d*"
              autoComplete="postal-code"
              enterKeyHint={enterKeyHint}
              disabled={disabled}
            />
          );

          if (!showCityState) {
            return zipInput;
          }

          // Stacked layout: each field gets its own full-width row so the Zip
          // field isn't squished into a narrow column.
          if (stacked) {
            return (
              <>
                {cityInput}
                {stateInput}
                {zipInput}
              </>
            );
          }

          // Default inline layout (City | State | Zip on one row).
          return (
            <Grid gutter={8}>
              <Grid.Col span={5}>{cityInput}</Grid.Col>
              <Grid.Col span={4}>{stateInput}</Grid.Col>
              <Grid.Col span={3}>{zipInput}</Grid.Col>
            </Grid>
          );
        })()}
      </Stack>
    </>
  );
};

export default AddressInput;
